/* eslint-disable @typescript-eslint/no-unsafe-assignment */

// Most examples are based on the official Vega-Lite documentation.

import { TopLevelSpec } from "vega-lite";
// Investigate: data loader instead of requiring inlined data?
const horizontalBar = {
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  description: "Connecticut Osprey Population",
  title: "CT Osprey Nest Count",
  data: {
    values: [
      { year: "2014", nests: 174 },
      { year: "2015", nests: 322 },
      { year: "2016", nests: 420 },
      { year: "2017", nests: 540 },
      { year: "2018", nests: 603 },
      { year: "2019", nests: 732 },
      { year: "2020", nests: 733 },
    ],
  },
  mark: "bar",
  encoding: {
    y: { field: "year", type: "nominal", axis: { labelAngle: 0 } },
    x: { field: "nests", type: "quantitative", axis: { grid: false } },
  },
  config: { view: { stroke: "transparent" } },
} as const;

const lineChartCustomWidth = {
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  description: "Connecticut Osprey Population",
  title: "CT Osprey Nest Count",
  data: {
    name: "table1",
  },
  mark: "line",
  encoding: {
    x: {
      field: "year",
      type: "nominal",
    },
    y: {
      field: "nests",
      type: "quantitative",
    },
  },
  width: 350,
} as const;

const heatmap = {
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  description: "A heatmap about penguin flipper length and body mass",
  data: {
    name: "table2",
  },
  mark: "rect",
  encoding: {
    x: {
      field: "Flipper Length (mm)",
      type: "quantitative",
      bin: { maxbins: 20 },
      scale: {
        zero: false,
      },
    },
    y: {
      field: "Body Mass (g)",
      type: "quantitative",
      bin: { maxbins: 20 },
      scale: {
        zero: false,
      },
    },
    color: {
      aggregate: "count",
      type: "quantitative",
    },
  },
} as const;

const scatterplot = {
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  description:
    "A scatterplot showing body mass and flipper lengths of penguins.",
  data: {
    name: "table2",
  },
  mark: "point",
  encoding: {
    x: {
      field: "Flipper Length (mm)",
      type: "quantitative",
      scale: { zero: false },
    },
    y: {
      field: "Body Mass (g)",
      type: "quantitative",
      scale: { zero: false },
    },
    color: { field: "Species", type: "nominal" },
    shape: { field: "Species", type: "nominal" },
  },
} as const;

const filteredScatterplot = {
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  description:
    "A filtered scatterplot showing body mass and flipper lengths of penguins.",
  data: {
    name: "table2",
  },
  transform: [
    {
      filter: {
        not: {
          field: "Species",
          equal: "Gentoo",
        },
      },
    },
  ],
  mark: "point",
  encoding: {
    x: {
      field: "Flipper Length (mm)",
      type: "quantitative",
      scale: {
        zero: false,
      },
    },
    y: {
      field: "Body Mass (g)",
      type: "quantitative",
      scale: {
        zero: false,
      },
    },
    color: {
      field: "Species",
      type: "nominal",
    },
    shape: {
      field: "Species",
      type: "nominal",
    },
  },
} as any;

const linearRegressionScatterplot = {
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  description:
    "A scatterplot showing body mass and flipper lengths of penguins.",
  data: {
    name: "table2",
  },
  transform: [
    {
      filter: {
        not: {
          field: "Species",
          equal: "Gentoo",
        },
      },
    },
  ],
  layer: [
    {
      mark: "point",
      encoding: {
        x: {
          field: "Flipper Length (mm)",
          type: "quantitative",
          scale: {
            zero: false,
          },
        },
        y: {
          field: "Body Mass (g)",
          type: "quantitative",
          scale: {
            zero: false,
          },
        },
        color: {
          field: "Species",
          type: "nominal",
        },
        shape: {
          field: "Species",
          type: "nominal",
        },
      },
    },
    {
      mark: {
        type: "line",
        color: "firebrick",
      },
      transform: [
        {
          regression: "Body Mass (g)",
          on: "Flipper Length (mm)",
        },
      ],
      encoding: {
        x: {
          field: "Flipper Length (mm)",
          type: "quantitative",
        },
        y: {
          field: "Body Mass (g)",
          type: "quantitative",
        },
      },
    },
  ],
} as any;

const rainbowBar = {
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  description: "Connecticut Osprey Population",
  title: "CT Osprey Nest Count",
  data: {
    values: [
      {
        year: "2014",
        nests: 174,
      },
      {
        year: "2015",
        nests: 322,
      },
      {
        year: "2016",
        nests: 420,
      },
      {
        year: "2017",
        nests: 540,
      },
      {
        year: "2018",
        nests: 603,
      },
      {
        year: "2019",
        nests: 732,
      },
      {
        year: "2020",
        nests: 733,
      },
    ],
  },
  mark: "bar",
  encoding: {
    y: {
      field: "year",
      type: "nominal",
      axis: {
        labelAngle: 0,
      },
    },
    x: {
      field: "nests",
      type: "quantitative",
    },
    color: {
      field: "year",
      type: "nominal",
      scale: {
        scheme: "tableau10",
      },
    },
  },
} as any;

const pie = {
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  description: "Reproducing http://robslink.com/SAS/democd91/pyramid_pie.htm",
  data: {
    values: [
      { category: "Sky", value: 75, order: 3 },
      { category: "Shady side", value: 10, order: 1 },
      { category: "Sunny side", value: 15, order: 2 },
    ],
  },
  mark: { type: "arc", outerRadius: 80 },
  encoding: {
    theta: {
      field: "value",
      type: "quantitative",
      scale: { range: [2.356_194_49, 8.639_379_797] },
      stack: true,
    },
    color: {
      field: "category",
      type: "nominal",
      scale: {
        domain: ["Sky", "Shady side", "Sunny side"],
        range: ["#416D9D", "#674028", "#DEAC58"],
      },
      legend: {
        orient: "none",
        title: null,
        columns: 1,
        legendX: 200,
        legendY: 80,
      },
    },
    order: {
      field: "order",
    },
  },
} as any;

const barFromDatasetsTab = {
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  description: "Edit my values from the Datasets tab",
  title: "CT Osprey Nest Count",
  data: {
    name: "table1",
  },
  mark: "bar",
  encoding: {
    y: { field: "year", type: "nominal", axis: { labelAngle: 0 } },
    x: { field: "nests", type: "quantitative" },
  },
} as const;

// Generated
const sineWave = {
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",

  width: 300,
  height: 150,
  data: {
    sequence: {
      start: 0,
      stop: 12.7,
      step: 0.1,
      as: "x",
    },
  },
  transform: [
    {
      calculate: "sin(datum.x)",
      as: "sin(x)",
    },
  ],
  mark: "line",
  encoding: {
    x: {
      field: "x",
      type: "quantitative",
    },
    y: {
      field: "sin(x)",
      type: "quantitative",
    },
  },
} as any;

const orthographicGraticule = {
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  width: 250,
  height: 250,
  projection: {
    type: "orthographic",
    rotate: [0, -45, 0],
  },
  data: {
    graticule: {
      step: [15, 15],
    },
  },
  mark: {
    type: "geoshape",
  },
} as any;

const barWithAnnotation = {
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  description: "Connecticut Osprey Population",
  title: "CT Osprey Nest Count",
  data: {
    name: "table1",
  },
  layer: [
    {
      mark: "bar",
      encoding: {
        y: {
          field: "year",
          type: "nominal",
          axis: {
            labelAngle: 0,
            label: null,
          },
          title: null,
        },
        x: {
          field: "nests",
          type: "quantitative",
          title: "nests",
        },
        color: {
          field: "year",
          type: "nominal",
          scale: {
            scheme: "viridis",
          },
          legend: null,
        },
      },
    },
    {
      mark: "rule",
      encoding: {
        x: {
          aggregate: "mean",
          field: "nests",
          type: "quantitative",
        },
        color: {
          value: "#d64541",
        },
        size: {
          value: 2,
        },
      },
    },
  ],
} as any;

const animalIsotype = {
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  config: { view: { stroke: "" } },
  width: 800,
  height: 200,
  data: {
    values: [
      { country: "Great Britain", animal: "cattle", col: 3 },
      { country: "Great Britain", animal: "cattle", col: 2 },
      { country: "Great Britain", animal: "cattle", col: 1 },
      { country: "Great Britain", animal: "pigs", col: 2 },
      { country: "Great Britain", animal: "pigs", col: 1 },
      { country: "Great Britain", animal: "sheep", col: 10 },
      { country: "Great Britain", animal: "sheep", col: 9 },
      { country: "Great Britain", animal: "sheep", col: 8 },
      { country: "Great Britain", animal: "sheep", col: 7 },
      { country: "Great Britain", animal: "sheep", col: 6 },
      { country: "Great Britain", animal: "sheep", col: 5 },
      { country: "Great Britain", animal: "sheep", col: 4 },
      { country: "Great Britain", animal: "sheep", col: 3 },
      { country: "Great Britain", animal: "sheep", col: 2 },
      { country: "Great Britain", animal: "sheep", col: 1 },
      { country: "United States", animal: "cattle", col: 9 },
      { country: "United States", animal: "cattle", col: 8 },
      { country: "United States", animal: "cattle", col: 7 },
      { country: "United States", animal: "cattle", col: 6 },
      { country: "United States", animal: "cattle", col: 5 },
      { country: "United States", animal: "cattle", col: 4 },
      { country: "United States", animal: "cattle", col: 3 },
      { country: "United States", animal: "cattle", col: 2 },
      { country: "United States", animal: "cattle", col: 1 },
      { country: "United States", animal: "pigs", col: 6 },
      { country: "United States", animal: "pigs", col: 5 },
      { country: "United States", animal: "pigs", col: 4 },
      { country: "United States", animal: "pigs", col: 3 },
      { country: "United States", animal: "pigs", col: 2 },
      { country: "United States", animal: "pigs", col: 1 },
      { country: "United States", animal: "sheep", col: 7 },
      { country: "United States", animal: "sheep", col: 6 },
      { country: "United States", animal: "sheep", col: 5 },
      { country: "United States", animal: "sheep", col: 4 },
      { country: "United States", animal: "sheep", col: 3 },
      { country: "United States", animal: "sheep", col: 2 },
      { country: "United States", animal: "sheep", col: 1 },
    ],
  },
  mark: { type: "point", filled: true },
  encoding: {
    x: { field: "col", type: "ordinal", axis: null },
    y: { field: "animal", type: "ordinal", axis: null },
    row: { field: "country", header: { title: "" } },
    shape: {
      field: "animal",
      type: "nominal",
      scale: {
        domain: ["person", "cattle", "pigs", "sheep"],
        range: [
          "M1.7 -1.7h-0.8c0.3 -0.2 0.6 -0.5 0.6 -0.9c0 -0.6 -0.4 -1 -1 -1c-0.6 0 -1 0.4 -1 1c0 0.4 0.2 0.7 0.6 0.9h-0.8c-0.4 0 -0.7 0.3 -0.7 0.6v1.9c0 0.3 0.3 0.6 0.6 0.6h0.2c0 0 0 0.1 0 0.1v1.9c0 0.3 0.2 0.6 0.3 0.6h1.3c0.2 0 0.3 -0.3 0.3 -0.6v-1.8c0 0 0 -0.1 0 -0.1h0.2c0.3 0 0.6 -0.3 0.6 -0.6v-2c0.2 -0.3 -0.1 -0.6 -0.4 -0.6z",
          "M4 -2c0 0 0.9 -0.7 1.1 -0.8c0.1 -0.1 -0.1 0.5 -0.3 0.7c-0.2 0.2 1.1 1.1 1.1 1.2c0 0.2 -0.2 0.8 -0.4 0.7c-0.1 0 -0.8 -0.3 -1.3 -0.2c-0.5 0.1 -1.3 1.6 -1.5 2c-0.3 0.4 -0.6 0.4 -0.6 0.4c0 0.1 0.3 1.7 0.4 1.8c0.1 0.1 -0.4 0.1 -0.5 0c0 0 -0.6 -1.9 -0.6 -1.9c-0.1 0 -0.3 -0.1 -0.3 -0.1c0 0.1 -0.5 1.4 -0.4 1.6c0.1 0.2 0.1 0.3 0.1 0.3c0 0 -0.4 0 -0.4 0c0 0 -0.2 -0.1 -0.1 -0.3c0 -0.2 0.3 -1.7 0.3 -1.7c0 0 -2.8 -0.9 -2.9 -0.8c-0.2 0.1 -0.4 0.6 -0.4 1c0 0.4 0.5 1.9 0.5 1.9l-0.5 0l-0.6 -2l0 -0.6c0 0 -1 0.8 -1 1c0 0.2 -0.2 1.3 -0.2 1.3c0 0 0.3 0.3 0.2 0.3c0 0 -0.5 0 -0.5 0c0 0 -0.2 -0.2 -0.1 -0.4c0 -0.1 0.2 -1.6 0.2 -1.6c0 0 0.5 -0.4 0.5 -0.5c0 -0.1 0 -2.7 -0.2 -2.7c-0.1 0 -0.4 2 -0.4 2c0 0 0 0.2 -0.2 0.5c-0.1 0.4 -0.2 1.1 -0.2 1.1c0 0 -0.2 -0.1 -0.2 -0.2c0 -0.1 -0.1 -0.7 0 -0.7c0.1 -0.1 0.3 -0.8 0.4 -1.4c0 -0.6 0.2 -1.3 0.4 -1.5c0.1 -0.2 0.6 -0.4 0.6 -0.4z",
          "M1.2 -2c0 0 0.7 0 1.2 0.5c0.5 0.5 0.4 0.6 0.5 0.6c0.1 0 0.7 0 0.8 0.1c0.1 0 0.2 0.2 0.2 0.2c0 0 -0.6 0.2 -0.6 0.3c0 0.1 0.4 0.9 0.6 0.9c0.1 0 0.6 0 0.6 0.1c0 0.1 0 0.7 -0.1 0.7c-0.1 0 -1.2 0.4 -1.5 0.5c-0.3 0.1 -1.1 0.5 -1.1 0.7c-0.1 0.2 0.4 1.2 0.4 1.2l-0.4 0c0 0 -0.4 -0.8 -0.4 -0.9c0 -0.1 -0.1 -0.3 -0.1 -0.3l-0.2 0l-0.5 1.3l-0.4 0c0 0 -0.1 -0.4 0 -0.6c0.1 -0.1 0.3 -0.6 0.3 -0.7c0 0 -0.8 0 -1.5 -0.1c-0.7 -0.1 -1.2 -0.3 -1.2 -0.2c0 0.1 -0.4 0.6 -0.5 0.6c0 0 0.3 0.9 0.3 0.9l-0.4 0c0 0 -0.4 -0.5 -0.4 -0.6c0 -0.1 -0.2 -0.6 -0.2 -0.5c0 0 -0.4 0.4 -0.6 0.4c-0.2 0.1 -0.4 0.1 -0.4 0.1c0 0 -0.1 0.6 -0.1 0.6l-0.5 0l0 -1c0 0 0.5 -0.4 0.5 -0.5c0 -0.1 -0.7 -1.2 -0.6 -1.4c0.1 -0.1 0.1 -1.1 0.1 -1.1c0 0 -0.2 0.1 -0.2 0.1c0 0 0 0.9 0 1c0 0.1 -0.2 0.3 -0.3 0.3c-0.1 0 0 -0.5 0 -0.9c0 -0.4 0 -0.4 0.2 -0.6c0.2 -0.2 0.6 -0.3 0.8 -0.8c0.3 -0.5 1 -0.6 1 -0.6z",
          "M-4.1 -0.5c0.2 0 0.2 0.2 0.5 0.2c0.3 0 0.3 -0.2 0.5 -0.2c0.2 0 0.2 0.2 0.4 0.2c0.2 0 0.2 -0.2 0.5 -0.2c0.2 0 0.2 0.2 0.4 0.2c0.2 0 0.2 -0.2 0.4 -0.2c0.1 0 0.2 0.2 0.4 0.1c0.2 0 0.2 -0.2 0.4 -0.3c0.1 0 0.1 -0.1 0.4 0c0.3 0 0.3 -0.4 0.6 -0.4c0.3 0 0.6 -0.3 0.7 -0.2c0.1 0.1 1.4 1 1.3 1.4c-0.1 0.4 -0.3 0.3 -0.4 0.3c-0.1 0 -0.5 -0.4 -0.7 -0.2c-0.3 0.2 -0.1 0.4 -0.2 0.6c-0.1 0.1 -0.2 0.2 -0.3 0.4c0 0.2 0.1 0.3 0 0.5c-0.1 0.2 -0.3 0.2 -0.3 0.5c0 0.3 -0.2 0.3 -0.3 0.6c-0.1 0.2 0 0.3 -0.1 0.5c-0.1 0.2 -0.1 0.2 -0.2 0.3c-0.1 0.1 0.3 1.1 0.3 1.1l-0.3 0c0 0 -0.3 -0.9 -0.3 -1c0 -0.1 -0.1 -0.2 -0.3 -0.2c-0.2 0 -0.3 0.1 -0.4 0.4c0 0.3 -0.2 0.8 -0.2 0.8l-0.3 0l0.3 -1c0 0 0.1 -0.6 -0.2 -0.5c-0.3 0.1 -0.2 -0.1 -0.4 -0.1c-0.2 -0.1 -0.3 0.1 -0.4 0c-0.2 -0.1 -0.3 0.1 -0.5 0c-0.2 -0.1 -0.1 0 -0.3 0.3c-0.2 0.3 -0.4 0.3 -0.4 0.3l0.2 1.1l-0.3 0l-0.2 -1.1c0 0 -0.4 -0.6 -0.5 -0.4c-0.1 0.3 -0.1 0.4 -0.3 0.4c-0.1 -0.1 -0.2 1.1 -0.2 1.1l-0.3 0l0.2 -1.1c0 0 -0.3 -0.1 -0.3 -0.5c0 -0.3 0.1 -0.5 0.1 -0.7c0.1 -0.2 -0.1 -1 -0.2 -1.1c-0.1 -0.2 -0.2 -0.8 -0.2 -0.8c0 0 -0.1 -0.5 0.4 -0.8z",
        ],
      },
      legend: null,
    },
    color: {
      field: "animal",
      type: "nominal",
      legend: null,
      scale: {
        domain: ["person", "cattle", "pigs", "sheep"],
        range: [
          "rgb(162,160,152)",
          "rgb(194,81,64)",
          "rgb(93,93,93)",
          "rgb(91,131,149)",
        ],
      },
    },
    opacity: { value: 1 },
    size: { value: 200 },
  },
} as any;

const emojiIsotype = {
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  config: { view: { stroke: "" } },
  width: 800,
  height: 200,
  data: {
    values: [
      { country: "Great Britain", animal: "pigs" },
      { country: "Great Britain", animal: "pigs" },
      { country: "Great Britain", animal: "cattle" },
      { country: "Great Britain", animal: "cattle" },
      { country: "Great Britain", animal: "cattle" },
      { country: "Great Britain", animal: "sheep" },
      { country: "Great Britain", animal: "sheep" },
      { country: "Great Britain", animal: "sheep" },
      { country: "Great Britain", animal: "sheep" },
      { country: "Great Britain", animal: "sheep" },
      { country: "Great Britain", animal: "sheep" },
      { country: "Great Britain", animal: "sheep" },
      { country: "Great Britain", animal: "sheep" },
      { country: "Great Britain", animal: "sheep" },
      { country: "Great Britain", animal: "sheep" },
      { country: "United States", animal: "pigs" },
      { country: "United States", animal: "pigs" },
      { country: "United States", animal: "pigs" },
      { country: "United States", animal: "pigs" },
      { country: "United States", animal: "pigs" },
      { country: "United States", animal: "pigs" },
      { country: "United States", animal: "cattle" },
      { country: "United States", animal: "cattle" },
      { country: "United States", animal: "cattle" },
      { country: "United States", animal: "cattle" },
      { country: "United States", animal: "cattle" },
      { country: "United States", animal: "cattle" },
      { country: "United States", animal: "cattle" },
      { country: "United States", animal: "cattle" },
      { country: "United States", animal: "cattle" },
      { country: "United States", animal: "sheep" },
      { country: "United States", animal: "sheep" },
      { country: "United States", animal: "sheep" },
      { country: "United States", animal: "sheep" },
      { country: "United States", animal: "sheep" },
      { country: "United States", animal: "sheep" },
      { country: "United States", animal: "sheep" },
    ],
  },
  transform: [
    {
      calculate: "{'cattle': '🐄', 'pigs': '🐖', 'sheep': '🐏'}[datum.animal]",
      as: "emoji",
    },
    { window: [{ op: "rank", as: "rank" }], groupby: ["country", "animal"] },
  ],
  mark: { type: "text", baseline: "middle" },
  encoding: {
    x: { field: "rank", type: "ordinal", axis: null },
    y: { field: "animal", type: "nominal", axis: null, sort: null },
    row: { field: "country", header: { title: "" } },
    text: { field: "emoji", type: "nominal" },
    size: { value: 65 },
  },
} as any;

type VegaLiteSpec = TopLevelSpec;
type Presets = Record<string, VegaLiteSpec>;

export const SPEC_PRESETS: Presets = {
  horizontalBar,
  pie,
  lineChartCustomWidth,
  heatmap,
  scatterplot,
  filteredScatterplot,
  linearRegressionScatterplot,
  barFromDatasetsTab,
  barWithAnnotation,
  animalIsotype,
  emojiIsotype,
  rainbowBar,
  sineWave,
  orthographicGraticule,
};
export {};
